/**
 * Бридж к дизайн-системе платформы (`@aseller/antd-theme`).
 *
 * До этого модуля тема жила в самодельном `themeConfig.ts` — ручной копии того же
 * пакета: те же Rubik/14/16-16-8/30-40/sizeUnit 5, только переписанные литералами и
 * без тёмного режима. Копия и не могла его иметь: `darkAlgorithm` выводит цвета, а
 * половина интерфейса была покрашена hex'ами мимо токенов и в тёмной теме оставалась
 * светлой (сообщено с платформы 2026-08-21).
 *
 * Источник истины по значениям — токены ASeller DS, те же, что у платформы-хоста.
 * Поэтому модуль внутри платформы выглядит её частью, а не гостем со своей палитрой.
 *
 * Про `HostTheme` здесь намеренно ничего нет: слой `shared` не знает о слое `app`
 * (STRUCTURE.md), и перевод темы хоста в режим делает вызывающая сторона. Union'ы
 * совпадают, так что перевод — это присваивание, а не конверсия.
 */

import { colorSemanticDark } from '@aseller/antd-theme';
import { getAntdTheme, type Mode } from '@aseller/antd-theme/antd';
import { theme, type ThemeConfig } from 'antd';
import { opaqueOn, readableOn, readableOnAll } from './readableColor';

/** Режим темы модуля: ровно то, чем его различает дизайн-система. */
export type ThemeMode = Mode;

/** Режим для standalone: своей страницей никто не управляет, тему задавать некому. */
export const STANDALONE_THEME_MODE: ThemeMode = 'light';

/**
 * `ThemeConfig` для `<ConfigProvider theme={...}>`.
 *
 * Оба режима используют один compact-алгоритм — размеры и отступы совпадают, поэтому
 * смена темы не сдвигает вёрстку; тёмный добавляет к нему `darkAlgorithm` только ради
 * цветов.
 */
export function appTheme(mode: ThemeMode): ThemeConfig {
  const base = withOpaqueRowHover(withReadableText(getAntdTheme(mode)));
  if (mode !== 'dark') return base;
  // Тёмная палитра DS (@aseller/antd-theme 0.2.2) отдаёт тегу по умолчанию тёмно-синий
  // текст `tag-tag-text` (#001029) на тёмно-сером фоне `tag-tag-bg-default` (#33333D) —
  // подписи в тегах не читаются (QA 08.10, WS 15587410, п.2: месяцы и объём продаж
  // в сводке прогноза на 60 месяцев). Берём основной текст тёмной темы, пока DS не
  // исправит свой компонентный токен.
  return {
    ...base,
    components: {
      ...base.components,
      Tag: { ...base.components?.Tag, defaultColor: colorSemanticDark['text-color-text-primary'] },
    },
  };
}

/**
 * Подсветка строки таблицы под курсором — непрозрачная.
 *
 * Дизайн-система задаёт её полупрозрачной (`#0010290F`, в тёмной — `#FFFFFF1F`). Обычной
 * ячейке это всё равно, а закреплённой колонке — нет: на наведении её фон становится
 * прозрачным, и прокрученные под неё ячейки проступают поверх ASIN и имени расчёта
 * (QA 14.10, WS 15587410). Тот же оттенок, сведённый с фоном карточки, выглядит так же
 * и ничего не пропускает.
 */
function withOpaqueRowHover(config: ThemeConfig): ThemeConfig {
  const hover = config.components?.Table?.rowHoverBg;
  if (!hover) return config;
  const bg = theme.getDesignToken(config).colorBgContainer;
  return {
    ...config,
    components: { ...config.components, Table: { ...config.components?.Table, rowHoverBg: opaqueOn(hover, bg) } },
  };
}

/**
 * Текстовые токены темы — читаемые (≥ 4,5) в обоих режимах.
 *
 * Сигнальные цвета дизайн-системы подобраны под заливки: как текст зелёный давал на
 * карточке 2,36, красный в тёмной теме — 2,17, жёлтый — 1,86, третичный текст — 3,08,
 * плейсхолдер — 1,76 (QA 09.10, WS 15587410: плашки метрик, «$10841», «CNY», «Объёмн. вес»).
 * Цвет не заменяется своим: оттенок темы доводится до нормы (readableColor.ts), и для
 * сигнальных — сразу на карточке и на своей цветной плашке, где он стоит в метриках.
 * На эти токены опираются `Typography type="success|warning|danger|secondary"` и роли
 * `useAppColors().successText` и т.п.
 */
function withReadableText(config: ThemeConfig): ThemeConfig {
  const token = theme.getDesignToken(config);
  const bg = token.colorBgContainer;
  const onBgAndTint = (color: string, tint: string) => readableOnAll(color, [bg, tint]);
  return {
    ...config,
    token: {
      ...config.token,
      colorSuccessText: onBgAndTint(token.colorSuccess, token.colorSuccessBg),
      colorErrorText: onBgAndTint(token.colorError, token.colorErrorBg),
      colorWarningText: onBgAndTint(token.colorWarning, token.colorWarningBg),
      colorInfoText: onBgAndTint(token.colorInfo, token.colorInfoBg),
      colorTextTertiary: token.colorTextSecondary,
      colorTextDescription: token.colorTextSecondary,
      colorTextPlaceholder: readableOn(token.colorTextPlaceholder, bg),
    },
  };
}
